/**
 * What the Backoffice tour says, and what it points at.
 *
 * Selectors target the TOP-LEVEL navigation only. A dropdown gets one step describing what is behind it,
 * rather than the tour driving Bootstrap menus open to point at individual entries — that is fragile in a
 * way that fails silently, and a new merchant's first question is where a thing lives, not how its page
 * works. There are 134 routes in this app; a tour of them would be a worse manual than none.
 *
 * A step whose selector matches nothing is dropped before the tour starts. That is the normal case here,
 * not an edge one: the menu is assembled from the signed-in user's role and the merchant's enabled
 * features, so an EMPLOYEE, an ACCOUNTANT or a merchant without the loyalty module simply sees fewer
 * entries — and the tour has to describe the Backoffice in front of THIS person.
 */
const steps = [
  {
    target: null,
    title: 'Velkommen til Backoffice',
    body: 'Her styrer du menyen, prisene, åpningstidene og oppgjøret. Vi går gjennom hovedmenyen på et par '
      + 'minutter, så du vet hvor ting ligger. Du kan hoppe over når som helst, og ta omvisningen på nytt '
      + 'fra Support-menyen.'
  },
  {
    target: 'a[href="/overview"]',
    title: 'Oversikt',
    body: 'Startsiden. Her ser du dagens tall, og — hvis du er ny — en sjekkliste som tar deg gjennom det '
      + 'som må på plass før gjestene kan bestille: tema, meny, betaling og utstyr.'
  },
  {
    target: 'a[href="/orders"]',
    title: 'Ordrer',
    body: 'Alle bestillinger, uansett kanal — kasse, kiosk, QR og nett. Herfra ser du detaljene i en ordre, '
      + 'sender kvitteringen på e-post og finner igjen gamle ordrer lenger tilbake enn kassa selv husker.'
  },
  {
    target: '#reportScrollingDropdown',
    title: 'Rapporter',
    body: 'Omsetning, kontantbeholdning, alkoholsalg, transaksjoner og SAF-T. Det er her regnskapsføreren '
      + 'din henter tallene, og her du går ved et bokettersyn.'
  },
  {
    target: 'a[href="/products"]',
    title: 'Produkter',
    body: 'Menyen din: kategorier, retter, priser, allergener og tilvalg. Det er her du kommer til å bruke '
      + 'mest tid, og endringer herfra slår gjennom på kassa, kiosken, QR-menyen og bestillingssiden i '
      + 'løpet av sekunder. Du trenger ikke gjøre noe på enhetene.'
  },
  {
    target: '#loyaltyDropdown',
    title: 'Lojalitet',
    body: 'Rabatter du kan gi gjestene dine. En rabatt kan settes opp til å gjelde alle kanaler — kassa, '
      + 'kiosken, QR-menyen og nettbestillingen — eller bare noen av dem.'
  },
  {
    target: 'a[href="/users"]',
    title: 'Brukere',
    body: 'De ansatte som skal bruke kassa. Hver bruker får sin egen PIN-kode, og alt som registreres — '
      + 'salg, rabatter, refusjoner og dagsoppgjør — bokføres på den som er logget inn.'
  },
  {
    target: 'a[href="/analytics"]',
    title: 'Innsikt',
    body: 'Hva som selger, når det selger og hvordan det utvikler seg. Nyttig når du skal bestemme hva som '
      + 'skal stå på menyen neste måned.'
  },
  {
    target: '#settingsDropdown',
    title: 'Innstillinger',
    body: 'Åpningstider, bord, levering, merverdiavgift og terminaler — og de tre du trenger tidlig: '
      + 'Website Builder for utseendet på bestillingssiden, Aktiver VIVA-betaling, og Abonnement og '
      + 'betaling.'
  },
  {
    target: '#supportDropdown',
    title: 'Support',
    body: 'Brukerveiledninger, hjelpevideoer og siden om bokettersyn. Her ligger også vilkårene, '
      + 'personvernerklæringen og databehandleravtalen — og det er herfra du starter denne omvisningen '
      + 'på nytt.'
  },
  {
    target: null,
    title: 'Det var alt',
    body: 'Du finner omvisningen igjen under Support → Opplæring, som er greit å vite når nye ansatte skal '
      + 'i gang. Står du fast, nås vi på support@cagista.com.'
  }
];

export default {
  all() {
    // A copy, so a filtered run cannot shorten the list for the next one.
    return steps.slice();
  }
};
